import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds for file downloads
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('shopify_tally_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - clear token. Reload so AuthContext + ProtectedRoute can
      // re-evaluate and either kick off install (with ?shop=) or send the user
      // to the public landing page.
      localStorage.removeItem('shopify_tally_token');
      localStorage.removeItem('shopify_tally_user');

      const path = window.location.pathname;
      const isPublic = path === '/' || path.startsWith('/auth') || path === '/pricing' || path === '/install';
      if (!isPublic) {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// API service functions
export const api = {
  // Authentication
  auth: {
    verify: (shop: string) => apiClient.post('/shopify/auth/verify', { shop }),
  },

  // Shop information
  shop: {
    getInfo: (shop: string) => apiClient.get(`/shopify/shop?shop=${shop}`),
  },

  // Orders
  orders: {
    getOrders: (params: any) => apiClient.get('/export/orders', { params }),
    getOrder: (id: string) => apiClient.get(`/export/orders/${id}`),
    exportOrders: (data: any) => apiClient.post('/export/orders', data),
    previewExport: (data: any) => apiClient.post('/export/preview', data),
  },

  // Export
  export: {
    downloadFile: (filename: string) => 
      apiClient.get(`/export/download/${filename}`, { responseType: 'blob' }),
    getHistory: () => apiClient.get('/export/history'),
    getTemplates: () => apiClient.get('/export/templates'),
    saveTemplate: (data: any) => apiClient.post('/export/templates', data),
    deleteTemplate: (id: string) => apiClient.delete(`/export/templates/${id}`),
  },

  // Configuration
  config: {
    getConfig: () => apiClient.get('/config'),
    updateConfig: (data: any) => apiClient.put('/config', data),
    getFieldMapping: () => apiClient.get('/config/field-mapping'),
    updateFieldMapping: (mapping: any) => 
      apiClient.put('/config/field-mapping', { mapping }),
    getExportDefaults: () => apiClient.get('/config/export-defaults'),
    updateExportDefaults: (defaults: any) => 
      apiClient.put('/config/export-defaults', { defaults }),
    getTallyFormats: () => apiClient.get('/config/tally-formats'),
    testConnections: () => apiClient.post('/config/test-connection'),
    resetConfig: () => apiClient.post('/config/reset'),
  },

  // Health check
  health: {
    check: () => apiClient.get('/health'),
    detailed: () => apiClient.get('/health/detailed'),
  },

  // Tally Sync
  sync: {
    // Tally configuration
    getTallyConfig: () => apiClient.get('/sync/tally-config'),
    saveTallyConfig: (config: any) => apiClient.put('/sync/tally-config', config),
    testTallyConnection: (config: any) => apiClient.post('/sync/tally-config/test', config),

    // Sync status
    getStats: () => apiClient.get('/sync/stats'),
    getOrdersSyncStatus: (params?: { status?: string; limit?: number; offset?: number }) =>
      apiClient.get('/sync/orders', { params }),
    getOrderSyncStatus: (orderId: string) => apiClient.get(`/sync/orders/${orderId}`),
    getSyncLogs: (limit?: number) => apiClient.get('/sync/logs', { params: { limit } }),

    // Sync actions
    triggerManualSync: () => apiClient.post('/sync/trigger'),
    syncOrder: (orderId: string) => apiClient.post(`/sync/orders/${orderId}`),
    retryFailed: () => apiClient.post('/sync/retry-failed'),

    // Queue status
    getQueueStatus: () => apiClient.get('/sync/queue'),
    getScheduledJobs: () => apiClient.get('/sync/scheduled-jobs'),
  },

  // Tally Agent
  agent: {
    // Registration
    generateRegistrationCode: () => apiClient.post('/agent/register'),

    // Status
    getStatus: () => apiClient.get('/agent/status'),
    revokeAgent: () => apiClient.delete('/agent/revoke'),

    // Logs
    getLogs: (params?: {
      level?: string;
      category?: string;
      limit?: number;
      offset?: number;
      startDate?: string;
      endDate?: string;
    }) => apiClient.get('/agent/logs', { params }),
    getLogStats: (daysBack?: number) =>
      apiClient.get('/agent/logs/stats', { params: { daysBack } }),
    exportLogs: (params?: {
      level?: string;
      category?: string;
      startDate?: string;
      endDate?: string;
    }) => apiClient.get('/agent/logs/export', { params, responseType: 'blob' }),
    clearLogs: (options?: { olderThanDays?: number; level?: string }) =>
      apiClient.delete('/agent/logs', { data: options }),

    // Test
    testSync: (orderId: string) =>
      apiClient.post('/agent/test-sync', { orderId }),

    // Admin
    getConnectedAgents: () => apiClient.get('/agent/connected-agents'),
  },

  // Subscription & Billing
  subscription: {
    // Plans
    getPlans: () => apiClient.get('/subscription/plans'),

    // Current subscription
    getCurrent: () => apiClient.get('/subscription/current'),

    // Subscribe to a plan
    subscribe: (planId: number, billingCycle: 'monthly' | 'yearly') =>
      apiClient.post('/subscription/subscribe', { planId, billingCycle }),

    // Verify payment after Razorpay checkout
    verifyPayment: (data: {
      razorpayOrderId: string;
      razorpayPaymentId: string;
      razorpaySignature: string;
    }) => apiClient.post('/subscription/verify-payment', data),

    // Change plan (upgrade/downgrade)
    changePlan: (newPlanId: number, immediate?: boolean) =>
      apiClient.post('/subscription/change-plan', { newPlanId, immediate }),

    // Cancel subscription
    cancel: (immediately?: boolean) =>
      apiClient.post('/subscription/cancel', { immediately }),

    // Reactivate cancelled subscription
    reactivate: () => apiClient.post('/subscription/reactivate'),

    // Usage statistics
    getUsage: (months?: number) =>
      apiClient.get('/subscription/usage', { params: { months } }),
    getDailyUsage: () => apiClient.get('/subscription/usage/daily'),

    // Billing history
    getBillingHistory: (params?: { limit?: number; offset?: number }) =>
      apiClient.get('/subscription/billing/history', { params }),
    getInvoice: (invoiceNumber: string) =>
      apiClient.get(`/subscription/billing/invoice/${invoiceNumber}`),
    downloadInvoicePdf: (invoiceNumber: string) =>
      apiClient.get(`/subscription/billing/invoice/${invoiceNumber}/pdf`, {
        responseType: 'blob',
      }),

    // Feature check
    hasFeature: (feature: string) =>
      apiClient.get(`/subscription/features/${feature}`),
  },
};

export default api;